import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, Loader2, Shield, Check, Eye, EyeOff, ArrowRight, Wifi, WifiOff, UserX, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const getErrorInfo = (err) => {
  // Network / server errors
  if (!err.response) {
    return {
      message: 'Cannot reach server. Check your connection.',
      icon: WifiOff,
      color: 'orange',
    };
  }
  const status = err.response?.status;
  const detail = err.response?.data?.detail || '';

  if (status === 429) return { message: 'Too many attempts. Please wait a minute.', icon: AlertCircle, color: 'amber' };
  if (status >= 500 || status === 520) return { message: 'Server is temporarily unavailable. Try again shortly.', icon: WifiOff, color: 'orange' };
  if (detail.includes('No account')) return { message: 'No account found with this email.', icon: UserX, color: 'red' };
  if (detail.includes('Incorrect password')) return { message: 'Incorrect password. Try again.', icon: KeyRound, color: 'red' };
  if (detail === 'MFA_REQUIRED') return { message: '', icon: null, color: '' }; // handled separately
  if (detail.includes('Invalid MFA')) return { message: 'Invalid verification code.', icon: AlertCircle, color: 'red' };
  return { message: detail || 'Something went wrong. Please try again.', icon: AlertCircle, color: 'red' };
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [errorInfo, setErrorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForgotDialog, setShowForgotDialog] = useState(false);
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { setMounted(true); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorInfo(null);
    setLoading(true);

    try {
      await login(email, password, showMfaInput ? mfaCode : null);
      setLoginSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1800);
    } catch (err) {
      if (err.isMfaRequired) {
        setShowMfaInput(true);
        setErrorInfo(null);
        setLoading(false);
        return;
      }
      setErrorInfo(getErrorInfo(err));
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowMfaInput(false);
    setMfaCode('');
    setErrorInfo(null);
  };

  const errorColorMap = {
    red: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.2)', text: '#f87171' },
    orange: { bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.2)', text: '#fb923c' },
    amber: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', text: '#fbbf24' },
  };

  return (
    <div className="login-page">
      <div className="login-glow" />

      {/* Success animation */}
      {loginSuccess && (
        <div className="success-overlay">
          {/* Particle burst */}
          {[...Array(24)].map((_, i) => (
            <div key={i} className="success-particle" style={{
              '--angle': `${i * 15}deg`,
              '--delay': `${i * 30}ms`,
              '--distance': `${80 + Math.random() * 60}px`,
              '--size': `${3 + Math.random() * 4}px`,
            }} />
          ))}
          {/* Expanding rings */}
          <div className="success-ring success-ring--1" />
          <div className="success-ring success-ring--2" />
          <div className="success-ring success-ring--3" />
          {/* Central icon */}
          <div className="success-icon">
            <div className="success-icon-inner">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
          </div>
          <p className="success-text">Welcome back</p>
        </div>
      )}

      <div className={`login-wrapper ${mounted ? 'is-visible' : ''} ${loginSuccess ? 'is-exiting' : ''}`}>
        <img
          src="https://customer-assets.emergentagent.com/job_popforge/artifacts/9qv32c8p_Color%20logo%20-%20no%20background.png"
          alt="SynPop"
          className="login-logo"
        />

        <div className="login-card">
          <form onSubmit={handleSubmit}>
            {/* Error banner */}
            {errorInfo?.message && (
              <div
                className="login-error"
                data-testid="login-error-msg"
                style={{
                  background: errorColorMap[errorInfo.color]?.bg,
                  borderColor: errorColorMap[errorInfo.color]?.border,
                  color: errorColorMap[errorInfo.color]?.text,
                }}
              >
                {errorInfo.icon && <errorInfo.icon className="w-4 h-4 flex-shrink-0" />}
                <span>{errorInfo.message}</span>
              </div>
            )}

            {/* MFA notice */}
            {showMfaInput && (
              <div className="login-mfa-notice">
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span>Two-factor authentication required</span>
              </div>
            )}

            {/* Email + Password */}
            {!showMfaInput && (
              <>
                <input
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="login-input"
                  autoComplete="email"
                />
                <div className="login-password-row">
                  <div className="login-password-field">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="login-input"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="login-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="login-forgot"
                    onClick={() => setShowForgotDialog(true)}
                    data-testid="forgot-password-btn"
                  >
                    Forgot?
                  </button>
                </div>
              </>
            )}

            {/* MFA Code */}
            {showMfaInput && (
              <div className="login-mfa-field">
                <input
                  type="text"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  data-testid="login-mfa-input"
                  className="login-input login-input--mfa"
                  maxLength={6}
                />
                <p className="login-mfa-hint">Enter the 6-digit code from your authenticator</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className={`login-btn ${loginSuccess ? 'login-btn--success' : ''}`}
              disabled={loading || loginSuccess || (showMfaInput && mfaCode.length !== 6)}
              data-testid="login-submit-btn"
            >
              {loginSuccess ? (
                <span className="login-btn-content">
                  <Check className="w-4 h-4" /> Authenticated
                </span>
              ) : loading ? (
                <span className="login-btn-content">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </span>
              ) : (
                <span className="login-btn-content">
                  {showMfaInput ? 'Verify' : 'Enter'} <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </button>

            {showMfaInput && (
              <button type="button" className="login-back" onClick={handleBackToLogin}>
                Back to login
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Forgot Dialog */}
      <Dialog open={showForgotDialog} onOpenChange={setShowForgotDialog}>
        <DialogContent className="sm:max-w-md bg-[#0d1528] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Reset Password</DialogTitle>
            <DialogDescription className="pt-2 text-slate-400">
              Password resets are managed by your administrator. Please contact your organization&apos;s admin.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 p-4 bg-[#5b7cff]/10 rounded-lg border border-[#5b7cff]/20">
            <p className="text-sm text-blue-300">
              <strong>Need help?</strong> Reach out to your SynPop administrator who can reset your password from the User Management panel.
            </p>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={() => setShowForgotDialog(false)} className="px-4 py-2 text-sm rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 transition-colors">
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #0f172a, #111827, #0b1024);
          background-size: 200% 200%;
          animation: gradientShift 18s ease infinite;
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .login-glow {
          position: absolute;
          width: 600px; height: 600px;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          background: radial-gradient(circle, rgba(90,120,255,0.18) 0%, rgba(90,120,255,0.05) 40%, transparent 70%);
          filter: blur(40px);
          z-index: 0;
          pointer-events: none;
        }

        .login-wrapper {
          position: relative; z-index: 1;
          display: flex; flex-direction: column; align-items: center;
          width: 100%; max-width: 400px; padding: 0 20px;
          opacity: 0; transform: translateY(12px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .login-wrapper.is-visible { opacity: 1; transform: translateY(0); }
        .login-wrapper.is-exiting { animation: wrapperExit 0.5s 0.1s ease forwards; }
        @keyframes wrapperExit {
          to { transform: scale(0.95) translateY(-20px); opacity: 0; filter: blur(4px); }
        }

        .login-logo { width: 110px; margin-bottom: 30px; opacity: 0.95; }

        .login-card {
          width: 100%; padding: 30px; border-radius: 14px;
          background: rgba(20,30,60,0.55);
          backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
          border: 1px solid rgba(255,255,255,0.06);
          box-shadow: 0 20px 40px rgba(0,0,0,0.45);
        }

        /* ===== Inputs ===== */
        .login-input {
          width: 100%; height: 44px; border-radius: 10px;
          background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0 14px; color: white; font-size: 14px;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .login-input::placeholder { color: rgba(255,255,255,0.3); }
        .login-input:focus {
          border-color: rgba(120,160,255,0.6);
          box-shadow: 0 0 0 2px rgba(120,160,255,0.15);
        }
        /* Fix autofill styling */
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus {
          -webkit-text-fill-color: white !important;
          -webkit-box-shadow: 0 0 0 1000px rgba(20,30,60,0.9) inset !important;
          box-shadow: 0 0 0 1000px rgba(20,30,60,0.9) inset !important;
          border-color: rgba(255,255,255,0.08) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        .login-input--mfa {
          text-align: center; font-size: 24px;
          letter-spacing: 0.5em; font-family: monospace; height: 52px;
        }

        .login-password-row { margin-top: 16px; display: flex; align-items: center; gap: 12px; }
        .login-password-field { flex: 1; position: relative; }
        .login-password-field .login-input { padding-right: 40px; }
        .login-eye-btn {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; color: rgba(255,255,255,0.4);
          cursor: pointer; padding: 0; display: flex; transition: color 0.15s;
        }
        .login-eye-btn:hover { color: rgba(255,255,255,0.7); }
        .login-forgot {
          background: none; border: none; color: rgba(255,255,255,0.45);
          font-size: 13px; cursor: pointer; white-space: nowrap; padding: 0;
          transition: color 0.15s;
        }
        .login-forgot:hover { color: rgba(255,255,255,0.85); }

        /* ===== Button ===== */
        .login-btn {
          width: 100%; height: 46px; margin-top: 18px; border-radius: 10px;
          background: linear-gradient(135deg, #5b7cff, #7aa2ff);
          color: white; font-weight: 500; font-size: 15px; border: none;
          cursor: pointer;
          transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease, background 0.4s ease;
        }
        .login-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(100,120,255,0.35);
        }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-btn--success {
          background: linear-gradient(135deg, #22c55e, #16a34a) !important;
          opacity: 1 !important;
          box-shadow: 0 0 30px rgba(34,197,94,0.3);
        }
        .login-btn-content { display: flex; align-items: center; justify-content: center; gap: 8px; }

        /* ===== Error ===== */
        .login-error {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; border-radius: 10px;
          border: 1px solid; font-size: 13px; margin-bottom: 16px;
          animation: errorShake 0.35s ease;
        }
        @keyframes errorShake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-5px); }
          40% { transform: translateX(5px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }

        .login-mfa-notice {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; border-radius: 10px;
          background: rgba(91,124,255,0.1); border: 1px solid rgba(91,124,255,0.2);
          color: #93b4ff; font-size: 13px; margin-bottom: 16px;
        }
        .login-mfa-field { text-align: center; }
        .login-mfa-hint { font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 8px; }
        .login-back {
          display: block; width: 100%; margin-top: 10px; text-align: center;
          background: none; border: none; color: rgba(255,255,255,0.4);
          font-size: 13px; cursor: pointer; transition: color 0.15s;
        }
        .login-back:hover { color: rgba(255,255,255,0.8); }

        /* ===== SUCCESS ANIMATION ===== */
        .success-overlay {
          position: fixed; inset: 0; z-index: 50;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          pointer-events: none;
        }

        /* Particles */
        .success-particle {
          position: absolute;
          width: var(--size); height: var(--size);
          border-radius: 50%;
          background: #5b7cff;
          top: 50%; left: 50%;
          animation: particleBurst 1s var(--delay) cubic-bezier(0.16,1,0.3,1) forwards;
          opacity: 0;
        }
        .success-particle:nth-child(odd) { background: #22c55e; }
        .success-particle:nth-child(3n) { background: #7aa2ff; }
        @keyframes particleBurst {
          0% { transform: translate(-50%,-50%) rotate(var(--angle)) translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translate(-50%,-50%) rotate(var(--angle)) translateY(calc(-1 * var(--distance))); opacity: 0; }
        }

        /* Rings */
        .success-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid rgba(91,124,255,0.4);
          animation: ringExpand 1.2s cubic-bezier(0.16,1,0.3,1) forwards;
          opacity: 0;
        }
        .success-ring--1 { width: 60px; height: 60px; animation-delay: 0s; }
        .success-ring--2 { width: 60px; height: 60px; animation-delay: 0.1s; border-color: rgba(34,197,94,0.3); }
        .success-ring--3 { width: 60px; height: 60px; animation-delay: 0.2s; border-color: rgba(122,162,255,0.2); }
        @keyframes ringExpand {
          0% { transform: scale(0.5); opacity: 1; }
          100% { transform: scale(5); opacity: 0; }
        }

        /* Central icon */
        .success-icon {
          position: relative; z-index: 2;
          animation: iconEntry 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
          opacity: 0;
        }
        .success-icon-inner {
          width: 64px; height: 64px; border-radius: 50%;
          background: linear-gradient(135deg, #5b7cff, #22c55e);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 0 40px rgba(91,124,255,0.4), 0 0 80px rgba(34,197,94,0.2);
          animation: iconPulse 1.5s 0.5s ease infinite;
        }
        @keyframes iconEntry {
          0% { transform: scale(0) rotate(-180deg); opacity: 0; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes iconPulse {
          0%,100% { box-shadow: 0 0 40px rgba(91,124,255,0.4), 0 0 80px rgba(34,197,94,0.2); }
          50% { box-shadow: 0 0 60px rgba(91,124,255,0.5), 0 0 120px rgba(34,197,94,0.3); }
        }

        .success-text {
          margin-top: 20px; color: white; font-size: 16px; font-weight: 500;
          letter-spacing: 0.05em; opacity: 0;
          animation: textFade 0.6s 0.4s ease forwards;
        }
        @keyframes textFade {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 0.9; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
